import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type { Offer, OfferLine } from "@/hooks/use-offers";
import {
  useAttachOfferToProject,
} from "@/hooks/use-convert-offer-to-project";
import { useAssignWritingCreditOnProject } from "@/hooks/use-assign-writing-credit";
import { useOfferSections } from "@/hooks/use-offer-sections";
import { TilvalgSelectStep } from "@/components/offer/TilvalgSelectStep";
import {
  selectTilvalgLines,
  sumLinesAmount,
} from "@/lib/select-convert-tilvalg";
import { offerLineEstimatedCost } from "@/lib/offer-to-project";
import { NewProjectWizard } from "@/components/new-project/NewProjectWizard";
import {
  DEFAULT_INVOICE_TERMS,
  type InvoiceTerm,
} from "@/components/offer/InvoiceTermsEditor";
import type { NewProjectInitialState } from "@/hooks/use-new-project-form";

interface Props {
  offer: Offer;
  lines: OfferLine[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * VIB-686: Konverterer tilbud til prosjekt via samme wizard som "Nytt
 * prosjekt"-siden bruker. Forskjellen er kun initialState (prefylt fra
 * tilbudet) og onCreated-callback som kobler tilbudet til det nye prosjektet
 * + krediterer tilbudsskriving på internprosjekt.
 *
 * Tidligere hadde denne dialogen sin egen mini-wizard med subset av feltene.
 * Det førte til scope-drift hver gang Nytt-prosjekt fikk nye felter
 * (referanser, faktureringsvilkår, mal-koblinger osv.). Nå deler de alt.
 */
export function ConvertOfferDialog({ offer, lines, open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const attachOffer = useAttachOfferToProject();
  const assignCredit = useAssignWritingCreditOnProject();
  // MEDIUM-19: prosjektet (+ PO-prosjekt) opprettes FØR tilbudet kobles. Hvis
  // koblingen feiler, husk project_id så et nytt forsøk kobler det EKSISTERENDE
  // prosjektet i stedet for å opprette et nytt (unngår duplikat prosjekt/PO-rad).
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

  // VIB-1142: tilvalgsseksjoner kunden kan velge før prosjektet opprettes.
  const { data: sections = [] } = useOfferSections(offer.id);
  const optionSections = useMemo(
    () => sections.filter((s) => s.section_type === "option"),
    [sections],
  );
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(
    new Set(),
  );
  const [tilvalgConfirmed, setTilvalgConfirmed] = useState(false);

  // VIB-1142: nullstill tilvalg-valg når dialogen lukkes, så et nytt
  // konverteringsforsøk alltid starter på valg-steget (komponenten forblir
  // montert mellom åpninger).
  useEffect(() => {
    if (!open) {
      setTilvalgConfirmed(false);
      setSelectedSectionIds(new Set());
    }
  }, [open]);

  // VIB-686: Map offer.invoice_terms (jsonb) til InvoiceTerm[]. Hvis tilbudet
  // mangler invoice_terms (gammelt tilbud), bygg en to-rad-liste fra
  // startup/final-prosenten — eller fall tilbake til 60/40-default.
  const initialInvoiceTerms = useMemo<InvoiceTerm[]>(() => {
    if (Array.isArray(offer.invoice_terms) && offer.invoice_terms.length > 0) {
      return offer.invoice_terms as InvoiceTerm[];
    }
    if (offer.startup_invoice_pct != null && offer.final_invoice_pct != null) {
      return [
        { label: "Oppstart", pct: offer.startup_invoice_pct },
        { label: "Levering", pct: offer.final_invoice_pct },
      ];
    }
    return DEFAULT_INVOICE_TERMS;
  }, [offer.invoice_terms, offer.startup_invoice_pct, offer.final_invoice_pct]);

  // Bygg initialState for wizard. Auto-genererte linjer (offer_writing-credit)
  // tas IKKE med i prosjektets budsjett — de håndteres separat i onCreated.
  const initialState = useMemo<Partial<NewProjectInitialState>>(() => {
    const customerLines = lines.filter(
      (l) =>
        !l.is_auto_generated ||
        l.auto_kind === "offer_writing",
    );
    // VIB-1142: kun valgte tilvalgsseksjoners linjer tas med; hovedlinjer alltid.
    const includedLines = selectTilvalgLines(customerLines, selectedSectionIds);
    return {
      customerId: offer.customer_id ?? "",
      projectName: offer.title,
      // VIB-1142: med tilvalg reflekter kontraktssum = hoved + valgte tilvalg
      // (ikke hele tilbudssummen). Uten tilvalgsseksjoner: uendret oppførsel.
      totalAmount:
        optionSections.length > 0
          ? String(sumLinesAmount(includedLines))
          : String(offer.total_amount ?? ""),
      offerOverheadPct: offer.overhead_pct ?? 0,
      offerDiscountPct: offer.discount_pct ?? 0,
      offerDiscountAmount: offer.discount_amount ?? 0,
      invoiceTerms: initialInvoiceTerms,
      // VIB-993: overfør "reise faktureres separat (medgått)" til prosjektet.
      billsTravelPerMedgatt: offer.bills_travel_per_medgatt ?? false,
      // VIB-2086: kopier grandfather-flagget så konvertering ikke endrer OH-regelen.
      includeTravelInOverhead: offer.include_travel_in_overhead === true,
      // Skjul PDF-opplastningen — vi har allerede budsjettlinjene fra tilbudet.
      pdfUploaded: true,
      budgetLines: includedLines.map((l) => ({
        id: String(Math.random()),
        phase: l.phase,
        description: l.description,
        // Tilbudslinjer bruker quantity+unit; "t" og "d" mapper til timer.
        quantity_hours:
          l.unit === "t"
            ? l.quantity ?? 0
            : l.unit === "d" && l.cost_type === "internal"
              ? (l.quantity ?? 0) * 8
              : 0,
        amount: l.amount ?? 0,
        // Innkjøpsprisen fra tilbudet blir est. kostnad på eksterne linjer.
        // Var hardkodet 0 fram til 23.10.2026, så BF estimert ble lik
        // kontraktssummen.
        estimated_cost: offerLineEstimatedCost(l),
        has_time:
          l.cost_type === "internal" && (l.unit === "t" || l.unit === "d"),
        cost_type: (l.cost_type as "internal" | "external") ?? "internal",
        // HIGH-18: bevar mal-kobling fra tilbudslinja (CLAUDE.md «Mal-kobling MÅ
        // persisteres»). template_name finnes ikke på OfferLine og leses ikke av
        // create-project.
        template_id: l.template_id ?? null,
        template_name: null,
        // VIB-995: linjer som tilhørte en offer-seksjon (section_type='option')
        // er tilvalg. Marker dem så de havner i Tilvalg-rubrikken på timetildeling.
        is_optional: l.section_id != null,
        // VIB-1158: bevar seksjonsnavnet så hvert tilvalg får sin egen rubrikk.
        option_label: l.section_id
          ? (sections.find((s) => s.id === l.section_id)?.name ?? null)
          : null,
        // VIB-2866: rekkefølgen fra tilbudet. Ble aldri sendt herfra, så
        // konverterte prosjekter fikk vilkårlig rekkefølge innen fasen.
        sort_order: l.sort_order ?? null,
      })),
    };
  }, [offer, lines, initialInvoiceTerms, selectedSectionIds, optionSections, sections]);

  // Kobler tilbud til (allerede opprettet) prosjekt + krediterer tilbudsskriving.
  // Trygt å kjøre på nytt mot samme project_id (idempotent retry ved feil).
  async function finishAttach(project_id: string) {
    try {
      // ── 1. Koble tilbud til prosjekt + lagre invoice-split for backward compat.
      const firstPct = initialInvoiceTerms[0]?.pct ?? 60;
      const lastPct = initialInvoiceTerms[initialInvoiceTerms.length - 1]?.pct ?? 40;
      await attachOffer.mutateAsync({
        offerId: offer.id,
        projectId: project_id,
        startupInvoicePct: firstPct,
        finalInvoicePct: lastPct,
      });

      // ── 2. Tilbudsskriving-credit: tildel timer på prosjektets Tilbudsskriving-linje
      // for hver writer i offer.writers. Stille feiler ikke konverteringen
      // hvis credit-loggingen feiler — tilbudet er tross alt opprettet.
      const writingLine = lines.find(
        (l) => l.is_auto_generated && l.auto_kind === "offer_writing",
      );
      if (writingLine && writingLine.quantity > 0 && (offer.writers ?? []).length > 0) {
        try {
          await assignCredit.mutateAsync({
            projectId: project_id,
            writers: offer.writers ?? [],
            totalHours: writingLine.quantity,
          });
        } catch (creditErr) {
          console.error("Tilbudsskriving auto-credit feilet:", creditErr);
        }
      }

      toast.success("Tilbud konvertert til prosjekt");
      setCreatedProjectId(null);
      onOpenChange(false);
      navigate(`/prosjekter/${project_id}`);
    } catch (err) {
      // Prosjektet er allerede opprettet — husk id-en så retry ikke lager nytt.
      setCreatedProjectId(project_id);
      toast.error(
        `Prosjektet ble opprettet, men kobling av tilbud feilet: ${(err as Error).message}. Prøv koblingen på nytt.`,
      );
    }
  }

  // Må returnere Promise slik at useCreateProject holder loading til
  // attach er ferdig — ellers kan «Opprett» klikkes igjen og lage dobbelt prosjekt.
  async function handleCreated({ project_id }: { project_id: string }) {
    await finishAttach(project_id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Konverter tilbud til prosjekt</DialogTitle>
          <DialogDescription>
            Oppretter et prosjekt i Sølvrev og PowerOffice fra dette tilbudet.
            Tilbudet kobles automatisk til det nye prosjektet.
          </DialogDescription>
        </DialogHeader>
        {createdProjectId ? (
          // Prosjektet er opprettet, men koblingen feilet — IKKE vis wizard
          // (ville opprettet nytt prosjekt). Tilby retry av kun koblingen.
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-muted-foreground">
              Prosjektet ble opprettet, men koblingen av tilbudet feilet. Prøv
              koblingen på nytt — det opprettes ikke et nytt prosjekt.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => void finishAttach(createdProjectId)}
                disabled={attachOffer.isPending}
              >
                Prøv kobling på nytt
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/prosjekter/${createdProjectId}`);
                }}
              >
                Gå til prosjektet
              </Button>
            </div>
          </div>
        ) : optionSections.length > 0 && !tilvalgConfirmed ? (
          <TilvalgSelectStep
            sections={optionSections}
            lines={lines}
            selected={selectedSectionIds}
            onToggle={(id) =>
              setSelectedSectionIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onConfirm={() => setTilvalgConfirmed(true)}
          />
        ) : (
          <NewProjectWizard
            initialState={initialState}
            onCreated={handleCreated}
            skipSteps={["upload", "budget"]}
            fromOffer
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
